/**
 * Shared column resolver — matches template columns to board columns at apply time.
 *
 * Matching identity is NORMALIZED TITLE + TYPE (with monday's type-name aliases).
 * Column ids are deliberately NOT used: monday column ids are board-scoped and reuse
 * default names ("status", "date4", ...), so an id can collide with an unrelated
 * column on another board.
 *
 * This module is mirrored in spt/src/helpers/column-matching.ts — any change here
 * must be applied there too, and both fixture test suites must stay identical.
 */
import { COLUMN_TYPE_NAME_MISMATCHES } from '../Config';

export function normalizeTitle(title) {
  return String(title ?? '')
    .trim()
    .toLowerCase();
}

function aliasType(type) {
  return COLUMN_TYPE_NAME_MISMATCHES[type] ?? type;
}

export function typesMatch(a, b) {
  if (a === b) return true;
  return aliasType(a) === b || a === aliasType(b) || aliasType(a) === aliasType(b);
}

/**
 * Resolve each template column against the board's columns.
 * - Template order is preserved; the first unused board column wins.
 * - Each board column is used at most once (duplicate titles resolve first-unused).
 *
 * @returns {{ idMap: Object, unmatched: Array }}
 */
export function resolveColumns(templateColumns, boardColumns) {
  const idMap = {};
  const unmatched = [];
  const used = new Set();

  for (const tc of templateColumns ?? []) {
    let found = false;
    for (const bc of boardColumns ?? []) {
      if (used.has(bc.id)) continue;
      if (normalizeTitle(tc.title) === normalizeTitle(bc.title) && typesMatch(tc.type, bc.type)) {
        idMap[String(tc.id)] = bc.id;
        used.add(bc.id);
        found = true;
        break;
      }
    }
    if (!found) unmatched.push(tc);
  }

  return { idMap, unmatched };
}
